import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Eye, Plus, RefreshCw, Send, ArrowLeft } from "lucide-react";
import {
  StatusPill, PriorityPill, SlaIndicator, StatsRow, MessageBubble, Modal,
} from "@/components/phase4/Phase4Common";

const CATEGORIES = [
  "SHIPMENT_QUERY", "PAYMENT_QUERY", "ACCOUNT_ISSUE", "TECHNICAL_ISSUE",
  "PARTNER_QUERY", "RATE_QUERY", "REFUND_REQUEST", "GENERAL",
];

export default function SupportTickets() {
  const { id } = useParams();
  if (id) return <TicketDetail id={id} />;
  return <TicketsList />;
}

function TicketsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isStaff = role === "admin" || role === "staff";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterMine, setFilterMine] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      if (filterPriority) params.priority = filterPriority;
      if (filterMine && isStaff) params.assigned_to = user?.id;
      const r = await api.get("/v2/support/tickets", { params });
      setItems(r.data?.items || []);
      if (isStaff) {
        try {
          const s = await api.get("/v2/support/tickets/stats");
          setStats(s.data);
        } catch {}
      }
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  }, [filterStatus, filterCategory, filterPriority, filterMine, isStaff, user?.id]);

  useEffect(() => { load(); }, [load]);

  const basePath = role === "admin" ? "/admin/support" : role === "staff" ? "/staff/support" : "/customer/support";

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="support-tickets">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Support Tickets</h1>
            <p className="text-slate-500 text-sm mt-0.5">{isStaff ? "Manage customer support requests" : "Get help from our team"}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => setRaiseOpen(true)}
              data-testid="ticket-raise-btn"
              className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Raise Ticket
            </button>
          </div>
        </div>

        {isStaff && stats && (
          <StatsRow items={[
            { key: "open", label: "Open", value: stats.open || 0, color: "text-red-600" },
            { key: "in_progress", label: "In Progress", value: stats.in_progress || 0, color: "text-blue-600" },
            { key: "awaiting", label: "Awaiting Cust.", value: stats.awaiting_customer || 0, color: "text-amber-600" },
            { key: "breached", label: "SLA Breached", value: stats.sla_breached || 0, color: "text-rose-600" },
            { key: "avg", label: "Avg Resolve (h)", value: (stats.avg_resolution_time_hrs ?? 0).toFixed(1), color: "text-emerald-600" },
          ]} />
        )}

        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="ticket-filter-status"
            className="text-xs border border-slate-200 rounded px-2 py-1.5">
            <option value="">All Statuses</option>
            {["OPEN", "IN_PROGRESS", "AWAITING_CUSTOMER", "RESOLVED", "CLOSED"].map((s) =>
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1.5">
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1.5">
            <option value="">All Priorities</option>
            {["URGENT", "HIGH", "MEDIUM", "LOW"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {isStaff && (
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={filterMine} onChange={(e) => setFilterMine(e.target.checked)}
                data-testid="ticket-filter-mine" /> My Tickets
            </label>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="tickets-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Ticket ID", "Subject", "Category", "Priority", "Status", "SLA", isStaff ? "Assigned" : "—", "Created", "Actions"].map((h, i) =>
                  <th key={i} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">No tickets.</td></tr>
                : items.map((t) => (
                  <tr key={t.ticket_id} className="hover:bg-slate-50" data-testid={`ticket-row-${t.ticket_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{t.ticket_id}</td>
                    <td className="px-3 py-2 text-xs text-slate-800 truncate max-w-[260px]">{t.subject}</td>
                    <td className="px-3 py-2 text-xs">{(t.category || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2"><PriorityPill priority={t.priority} /></td>
                    <td className="px-3 py-2"><StatusPill status={t.status} /></td>
                    <td className="px-3 py-2"><SlaIndicator deadline={t.sla_deadline} breached={t.sla_breached} resolved={t.resolved_at} /></td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{t.assigned_to?.slice(-6) || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => navigate(`${basePath}/${t.ticket_id}`)}
                        data-testid={`ticket-view-${t.ticket_id}`}
                        className="text-brand-500 hover:text-brand-700">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {raiseOpen && (
        <RaiseTicketModal
          role={role}
          onClose={() => setRaiseOpen(false)}
          onCreated={() => { setRaiseOpen(false); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function TicketDetail({ id }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isStaff = role === "admin" || role === "staff";
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [statusVal, setStatusVal] = useState("");
  const [resolution, setResolution] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v2/support/tickets/${id}`);
      setT(r.data);
      setStatusVal(r.data?.status || "");
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isStaff) return;
    api.get("/users", { params: { role: "staff", limit: 100 } })
      .then((r) => setStaffList(r.data?.items || r.data || []))
      .catch(() => {});
  }, [isStaff]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/v2/support/tickets/${id}/reply`, { content: reply, is_internal: internal });
      toast.success("Reply sent");
      setReply(""); setInternal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const updateStatus = async () => {
    if (!statusVal) return;
    if (["RESOLVED", "CLOSED"].includes(statusVal) && !resolution.trim()) {
      toast.error("Resolution required for RESOLVED/CLOSED");
      return;
    }
    try {
      await api.put(`/v2/support/tickets/${id}/status`, { status: statusVal, resolution });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doAssign = async () => {
    if (!assignTo) return;
    try {
      await api.put(`/v2/support/tickets/${id}/assign`, { assigned_to: assignTo });
      toast.success("Assigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const basePath = role === "admin" ? "/admin/support" : role === "staff" ? "/staff/support" : "/customer/support";
  if (loading || !t) return <DashboardLayout><div className="p-8 text-slate-400">Loading ticket…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="ticket-detail-page">
        <button onClick={() => navigate(basePath)} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to tickets
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700">
                  {(t.category || "").replace(/_/g, " ")}
                </span>
                <PriorityPill priority={t.priority} />
                <StatusPill status={t.status} />
                <SlaIndicator deadline={t.sla_deadline} breached={t.sla_breached} resolved={t.resolved_at} />
              </div>
              <h2 className="font-heading text-lg font-bold text-slate-900">{t.subject}</h2>
              <div className="text-xs text-slate-500 mt-1">Ticket {t.ticket_id} · Created {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</div>
              {t.shipment_id && (
                <div className="mt-2 text-xs">
                  <span className="text-slate-500">Linked shipment:</span>{" "}
                  <span className="font-mono text-brand-600">{t.shipment_id}</span>
                </div>
              )}
              {t.first_response_at && (
                <div className="mt-1 text-xs text-emerald-600">
                  First response: {new Date(t.first_response_at).toLocaleString()}
                </div>
              )}
              {t.resolution && (
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                  <span className="font-semibold text-emerald-700">Resolution:</span> {t.resolution}
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="font-heading text-sm font-bold text-slate-900 mb-3">Discussion</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="ticket-thread">
                {(t.messages || []).length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-4">No messages yet</div>
                ) : (t.messages || []).map((m, i) => <MessageBubble key={m.message_id || i} msg={m} />)}
              </div>
              <div className="mt-3 border-t pt-3 space-y-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                  data-testid="ticket-reply-input"
                  placeholder="Reply…"
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
                <div className="flex items-center justify-between">
                  {isStaff ? (
                    <label className="text-xs text-slate-600 flex items-center gap-1">
                      <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)}
                        data-testid="ticket-reply-internal" />
                      Internal note
                    </label>
                  ) : <span />}
                  <button onClick={sendReply} disabled={busy || !reply.trim()}
                    data-testid="ticket-reply-send"
                    className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded disabled:opacity-50 inline-flex items-center gap-1">
                    <Send className="w-3.5 h-3.5" /> Send Reply
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isStaff && (
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">Assign</h4>
                <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
                  data-testid="ticket-assign-select"
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 mb-2">
                  <option value="">— Select staff —</option>
                  {staffList.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name || s.email}</option>)}
                </select>
                <button onClick={doAssign} disabled={!assignTo}
                  data-testid="ticket-assign-btn"
                  className="w-full px-3 py-1.5 bg-slate-700 text-white text-xs font-semibold rounded disabled:opacity-50">
                  Assign
                </button>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">Update Status</h4>
                <select value={statusVal} onChange={(e) => setStatusVal(e.target.value)}
                  data-testid="ticket-status-select"
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 mb-2">
                  {["OPEN", "IN_PROGRESS", "AWAITING_CUSTOMER", "RESOLVED", "CLOSED"].map((s) =>
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                {["RESOLVED", "CLOSED"].includes(statusVal) && (
                  <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={2}
                    data-testid="ticket-resolution"
                    placeholder="Resolution notes (required)"
                    className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 mb-2" />
                )}
                <button onClick={updateStatus}
                  data-testid="ticket-status-btn"
                  className="w-full px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded">
                  Update Status
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function RaiseTicketModal({ role, onClose, onCreated }) {
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [shipmentId, setShipmentId] = useState("");
  const [busy, setBusy] = useState(false);

  const isCustomer = role === "customer";
  const allowedPriorities = isCustomer ? ["LOW", "MEDIUM", "HIGH"] : ["LOW", "MEDIUM", "HIGH", "URGENT"];

  const submit = async () => {
    if (!category || !subject.trim() || !desc.trim()) { toast.error("Fill all required fields"); return; }
    setBusy(true);
    try {
      await api.post("/v2/support/tickets", {
        category, subject, description: desc, priority,
        shipment_id: shipmentId || undefined,
      });
      toast.success("Ticket raised");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Raise Ticket" testId="ticket-raise-modal">
      <div className="space-y-3 text-sm">
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Category *</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="ticket-raise-category"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
            <option value="">— Select —</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Subject *</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)}
            data-testid="ticket-raise-subject"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Description *</label>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4}
            data-testid="ticket-raise-desc"
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
              data-testid="ticket-raise-priority"
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">
              {allowedPriorities.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Linked AWB / Shipment</label>
            <input value={shipmentId} onChange={(e) => setShipmentId(e.target.value)}
              data-testid="ticket-raise-shipment"
              placeholder="optional"
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
        <button onClick={submit} disabled={busy}
          data-testid="ticket-raise-submit"
          className="px-4 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded disabled:opacity-50">
          Raise Ticket
        </button>
      </div>
    </Modal>
  );
}
